/**
 * The four-layer init-config merge, resolved in the browser.
 *
 * This is a deliberate second copy of rules that live in Python
 * (`core/capabilities/init_resolver.py:build_capability_init_kwargs` and
 * `core/field_scopes.py:filter_by_scope`). The backend returns only the merged
 * result with no record of which layer won, so a view that explains the merge
 * has to redo it. Every input is already fetchable, so this needs no endpoint.
 *
 * It is a copy, so it can drift. The mitigation is that the rules are small,
 * stated once here, and covered by tests that encode each one — and the
 * follow-up is a `…/resolved` endpoint returning `{value, source, locked}` so
 * precedence lives in one place. Change the Python and this must change too.
 *
 * The rules, in the order the backend applies them:
 *
 *   1. Start from the capability's own field defaults.
 *   2. Global config overrides, unconditionally — it is the base layer an
 *      operator controls.
 *   3. Org config overrides ONLY where `field_scopes[field].organization` is
 *      exactly `true`. Anything else stored at the org layer is discarded.
 *   4. User config overrides ONLY where `field_scopes[field].user` is `true`.
 *   5. Identity fields are stripped — the framework supplies them.
 *
 * Absent field or flag means locked, so the default posture is "global wins".
 */
import type { FieldScopes, FieldSpec } from '@optum/api-client/types';

export const CONFIG_LAYERS = ['default', 'global', 'organization', 'user'] as const;
export type ConfigLayer = (typeof CONFIG_LAYERS)[number];

/** Supplied by the framework at construction; never read from config. */
const IDENTITY_FIELDS: ReadonlySet<string> = new Set(['agent_id', 'capability_id', 'org_id']);

export interface LayerValue {
  present: boolean;
  value: unknown;
  /**
   * Stored at this layer, but the field is not unlocked for it — the backend
   * drops it during the merge. Rendered struck through: the point of this view
   * is to explain why a value someone saved never takes effect.
   */
  ignored: boolean;
}

export interface ResolvedField {
  name: string;
  spec: FieldSpec;
  layers: Record<ConfigLayer, LayerValue>;
  /** The layer that won, or `unset` when nothing supplied a value. */
  source: ConfigLayer | 'unset';
  value: unknown;
  /** No lower layer may override this field. */
  lockedToGlobal: boolean;
}

export interface ResolveInput {
  fields: readonly FieldSpec[];
  fieldScopes: FieldScopes;
  global: Record<string, unknown>;
  organization: Record<string, unknown>;
  user: Record<string, unknown>;
}

/**
 * Key presence, not `!== undefined`.
 *
 * The Python iterates the stored dict, so a field explicitly saved as `null`
 * is a value and wins its layer. Treating it as absent would show the layer
 * below winning, which is the opposite of what would run.
 */
function has(config: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(config, key);
}

function unlocked(scopes: FieldScopes, field: string, layer: 'organization' | 'user'): boolean {
  return scopes[field]?.[layer] === true;
}

export function resolveInitConfig({
  fields,
  fieldScopes,
  global,
  organization,
  user,
}: ResolveInput): ResolvedField[] {
  return fields
    .filter((spec) => !IDENTITY_FIELDS.has(spec.name))
    .map((spec) => {
      const name = spec.name;
      const orgOk = unlocked(fieldScopes, name, 'organization');
      const userOk = unlocked(fieldScopes, name, 'user');

      const layers: Record<ConfigLayer, LayerValue> = {
        default: { present: spec.has_default, value: spec.default, ignored: false },
        global: { present: has(global, name), value: global[name], ignored: false },
        organization: {
          present: has(organization, name),
          value: organization[name],
          ignored: has(organization, name) && !orgOk,
        },
        user: {
          present: has(user, name),
          value: user[name],
          ignored: has(user, name) && !userOk,
        },
      };

      let source: ConfigLayer | 'unset' = 'unset';
      let value: unknown;
      if (layers.default.present) {
        source = 'default';
        value = layers.default.value;
      }
      if (layers.global.present) {
        source = 'global';
        value = layers.global.value;
      }
      if (orgOk && layers.organization.present) {
        source = 'organization';
        value = layers.organization.value;
      }
      if (userOk && layers.user.present) {
        source = 'user';
        value = layers.user.value;
      }

      return { name, spec, layers, source, value, lockedToGlobal: !orgOk && !userOk };
    });
}

/** A `{"$secret": "KEY"}` reference, which only the backend can dereference. */
export function secretRef(value: unknown): string | null {
  if (typeof value !== 'object' || value === null) return null;
  const ref = (value as Record<string, unknown>)['$secret'];
  return typeof ref === 'string' ? ref : null;
}

export function describeValue(value: unknown, present = true): string {
  if (!present) return '—';
  const ref = secretRef(value);
  if (ref) return `$secret:${ref}`;
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (typeof value === 'string') return value === '' ? '""' : value;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
